import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import {
  DEFAULT_DESIGN_MAP_LAYER_SETTINGS,
  DESIGN_MAP_LAYER_SETTINGS_STORAGE_KEY,
  parseDesignMapLayerSettings,
  type DesignMapLayerSettings,
} from '../helpers/design-map-layer-settings.helpers';

const { useStore, set, get } = createLocalStorageStore<DesignMapLayerSettings>(
  DESIGN_MAP_LAYER_SETTINGS_STORAGE_KEY,
  DEFAULT_DESIGN_MAP_LAYER_SETTINGS,
);

/**
 * Current Design map Show-checkbox state (localStorage).
 */
export const getDesignMapLayerSettings = (): DesignMapLayerSettings =>
  parseDesignMapLayerSettings(get());

/**
 * Replace the stored Show-checkbox state.
 */
export const setDesignMapLayerSettings = (
  next: DesignMapLayerSettings,
): void => {
  set(parseDesignMapLayerSettings(next));
};

/**
 * Merge a patch into the stored Show-checkbox state.
 */
export const patchDesignMapLayerSettings = (
  patch: Partial<DesignMapLayerSettings>,
): DesignMapLayerSettings => {
  const next: DesignMapLayerSettings = {
    ...getDesignMapLayerSettings(),
    ...patch,
  };
  set(next);
  return next;
};

export const useDesignMapLayerSettings = (): DesignMapLayerSettings =>
  parseDesignMapLayerSettings(useStore());

export const useShowBackgroundMap = (): boolean =>
  useDesignMapLayerSettings().showBackgroundMap;

export const useShowNearbyBuildings = (): boolean =>
  useDesignMapLayerSettings().showNearbyBuildings;

export const useShowBlueprintUnderlay = (): boolean =>
  useDesignMapLayerSettings().showBlueprintUnderlay;

export const setShowBackgroundMap = (showBackgroundMap: boolean): void => {
  patchDesignMapLayerSettings({ showBackgroundMap });
};

export const setShowNearbyBuildings = (showNearbyBuildings: boolean): void => {
  patchDesignMapLayerSettings({ showNearbyBuildings });
};

export const setShowBlueprintUnderlay = (
  showBlueprintUnderlay: boolean,
): void => {
  patchDesignMapLayerSettings({ showBlueprintUnderlay });
};

export const useSetShowBackgroundMap = (): typeof setShowBackgroundMap =>
  setShowBackgroundMap;

export const useSetShowNearbyBuildings = (): typeof setShowNearbyBuildings =>
  setShowNearbyBuildings;

export const useSetShowBlueprintUnderlay =
  (): typeof setShowBlueprintUnderlay => setShowBlueprintUnderlay;
